import * as React from "react";
import { formSet, useCpFormAnyValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { BasePathContext, useBasePath } from "../../../reactContexts/basePathContext";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ContentLayout } from "../../../components/layout/contentLayout";
import { SingleNavMenuLink, toLinkButton, toLinkPage, toLinkSpace } from "../../identity/useNavMenu";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../../envVariables";
import { SingleNavMenu } from "../../../components/antd/layout/singleNavMenu";
import { General } from "../draft/general/general";
import { WorkloadOptionsTabs } from "../draft/optionsTabs";
import { WorkloadContainersTabs } from "../draft/containersTabs";
import { WorkloadJob } from "../draft/job";
import { WorkloadIdentityLink } from "../draft/identity";
import { WorkloadFirewallConfig } from "../draft/firewallConfig/firewallConfig";
import { WorkloadLoadBalancer } from "../draft/loadBalancer";
import { WorkloadAdvanced } from "../draft/advanced";
import { WorkloadSidecarEnvoy } from "../draft/sidecarEnvoy";
import { CacheProcessor } from "../draft/container/CacheProcessor";
import { ImageProcessor } from "../draft/container/ImageProcessor";
import { GvcLocationFetcher } from "../draft/GvcLocationFetcher";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { Button, Modal } from "antd";
import { CpSwitch } from "../../gvc/testNewForm/CpSwitch";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { WorkloadInput } from "../../../schema/types/workload/input";
import { store } from "../../../store";
import { CpRootError } from "../../gvc/testNewForm/CpRootError";
import { getUsedSecretLinks } from "../draft/utils/utils";

interface Props {
  ignoreBlockerRef: React.RefObject<boolean>;
  validator: (v: WorkloadInput) => Promise<boolean>;
}
export const WorkloadCreateContent: React.FC<Props> = ({ ignoreBlockerRef, validator }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/workload/-create/*`);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { id: formId, submit } = useCpFormContext();

  const type = useCpFormValue(`type`);
  const submitting = useCpFormAnyValue(`meta.submitting`);
  const inboundPublic = useCpFormValue(`firewallConfig.external.inbound.public`);

  const [showFirewallModal, setShowFirewallModal] = React.useState(false);
  const [showIdentityModal, setShowIdentityModal] = React.useState(false);

  async function localHandleSubmit() {
    // Validate the form first
    const form = store.getState().form.instances[formId];
    const values: WorkloadInput = form.values as WorkloadInput;
    const hasErrors = await validator(values);

    if (hasErrors) {
      formSet(formId, "meta.submitted", true);
      // Validation failed, don't show modal - errors will be displayed by the form
      return;
    }

    // Check identity requirements
    const usedSecretLinks = getUsedSecretLinks(values.containers);
    const identityIsRequired = usedSecretLinks.length > 0;
    if (identityIsRequired) {
      const hasIdentity = !!values.identity.value?.value;
      const identityIsValid = values.identity.isValid;
      const identityIsValidating = values.identity.isValidating;

      if (!hasIdentity || (!identityIsValid && !identityIsValidating)) {
        setShowIdentityModal(true);
        return;
      }
    }

    if (!inboundPublic && type !== "cron") {
      setShowFirewallModal(true);
      return;
    }
    await submit();
  }

  function handleIdentityModalIgnore() {
    setShowIdentityModal(false);
    if (!inboundPublic && type !== "cron") {
      setShowFirewallModal(true);
    } else {
      submit();
    }
  }

  async function handleFirewallModalConfirm() {
    setShowFirewallModal(false);
    await submit();
  }

  React.useEffect(() => {
    const startingPage = `${basePath}/general`;
    navigate(startingPage, { replace: true });
  }, []);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Workload", "/general", { names: [`name`, `description`, `type`, `gvc`] }),
      toLinkPage("Options", "/options", { names: [`defaultOptions`, `localOptions`] }),
      toLinkPage("Containers", "/containers", { names: [`containers`, `containersservestrafficroot`] }),
    );

    if (type === "cron") {
      links.push(toLinkPage("Job", "/job", { names: ["job"] }));
    }

    links.push(
      toLinkPage("Identity", "/identity-link", { names: ["identity"] }),
      toLinkPage("Firewall", "/firewall", { names: ["firewallConfig"] }),
    );

    if (type !== "cron") {
      links.push(toLinkPage("Load Balancer", "/load-balancer", { names: ["loadBalancer"] }));
    }

    links.push(
      toLinkPage("Advanced Settings", "/advanced-settings", {
        names: ["supportDynamicTags", "rolloutOptions", "securityOptions", "extras", "requestRetryPolicy"],
      }),
    );

    if (type !== "cron") {
      links.push(toLinkPage("Sidecar | Envoy", "/sidecar-envoy", { names: ["sidecar"] }));
    }

    links.push(
      toLinkPage("Tags", "/tags", { names: ["tags"] }),
      toLinkSpace(),
      toLinkButton("Cancel", {
        type: "primary",
        onClick: () => {
          ignoreBlockerRef.current = true;
          const back = pathname.split(`/-create`)[0];
          dispatch(setRedirect(back));
        },
        disabled: submitting,
        danger: true,
      }),
      toLinkButton("Preview", {
        type: "default",
        onClick: () => formSet(formId, `meta.previewing`, true),
        disabled: submitting,
      }),
      toLinkButton("Create", {
        type: "primary",
        onClick: async () => {
          await localHandleSubmit();
        },
        loading: submitting,
        disabled: submitting,
        className: "action",
      }),
    );

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
        }),
      );
    }

    return links;
  }, [type, submitting, inboundPublic]);

  return (
    <BasePathContext.Provider value={basePath}>
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={false} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`general/*`} element={<General />} />
              <Route path={`options/*`} element={<WorkloadOptionsTabs />} />
              <Route path={`containers/*`} element={<WorkloadContainersTabs />} />
              <Route path={`job/*`} element={<WorkloadJob />} />
              <Route path={`identity-link/*`} element={<WorkloadIdentityLink />} />
              <Route path={`firewall/*`} element={<WorkloadFirewallConfig />} />
              <Route path={`load-balancer/*`} element={<WorkloadLoadBalancer />} />
              <Route path={`advanced-settings/*`} element={<WorkloadAdvanced />} />
              <Route path={`sidecar-envoy/*`} element={<WorkloadSidecarEnvoy />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
            </Routes>
            <CacheProcessor />
            <ImageProcessor />
            <GvcLocationFetcher />
          </div>
        }
      />
      <Modal
        title="Firewall Warning"
        open={showFirewallModal}
        onCancel={() => setShowFirewallModal(false)}
        okText="Create"
        onOk={handleFirewallModalConfirm}
      >
        <p className="mb-4">
          This workload will not be accessible from the internet because the firewall inbound is not public.
        </p>
        <div className="flex items-center gap-2">
          <CpLabel name="firewallConfig.external.inbound.public">Make Public</CpLabel>
          <CpSwitch name="firewallConfig.external.inbound.public" />
        </div>
      </Modal>
      <Modal
        title="Identity Warning"
        open={showIdentityModal}
        onCancel={() => setShowIdentityModal(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setShowIdentityModal(false);
                navigate(`${basePath}/identity-link`);
              }}
            >
              Go To Identity
            </Button>
            <Button type="primary" onClick={handleIdentityModalIgnore}>
              Ignore and Create the Workload
            </Button>
          </div>
        }
      >
        <p>
          This workload uses secrets but does not have a valid identity configured. The workload may not be able to
          access the secrets it needs.
        </p>
      </Modal>
    </BasePathContext.Provider>
  );
};
